/* eslint-disable no-dupe-class-members */
import {chain} from 'lodash';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';

import qs, {ParseOptions, ParsedUrl, StringifyOptions} from 'query-string';

const queryParams = <const>[
    'landuseType',
    'listingType',
    'propertyType_IN',
    'features_IN',
    'tags_IN',

    'numBed',
    'minBed',
    'propertyFeatureIds',
    'propertyTagIds',
    'minAge',
    'maxAge',
    'minFloorArea',
    'maxFloorArea',
    'havePicture',
    'listingTypeId',
    'landuseTypeIds',
    'propertyTypeIds',
    'location',
    'depositOnly',
    'page',
    'sort',
];

const pathParams = <const>[
    'maxPrice',
    'minPrice',
    'minArea',
    'maxArea',
    'minRent',
    'maxRent',
    'minDeposit',
    'maxDeposit',
    'locationStr',
    'propertyTypeStr',
    'listingTypeStr',
    // 'countryLocaleStr',
];

export const parsedOption: ParseOptions = <const>{
    decode: true,
    parseBooleans: true,
    arrayFormat: 'comma',
};
export const stringifyOption: StringifyOptions = <const>{
    arrayFormat: 'comma',
};

const defaultParams = <const>[...pathParams, ...queryParams];

export type DefaultParamsType = (typeof defaultParams)[number];
export type QueryParamsType = (typeof queryParams)[number];

export type UrlParamsType = Record<(typeof pathParams)[number], string>;

export type AddtionalParamsType<T extends string> = Record<QueryParamsType | T,
    string | string[] | any[]>;

export default class UrlValidation<T extends string = DefaultParamsType> {
    private _parsedUrlObj!: ParsedUrl;

    private _urlObj!: URL;

    private _searchParams!: Partial<AddtionalParamsType<T>>;

    private _pathParams!: Partial<UrlParamsType>;

    private _additionaleParams: string[] = [];

    private readonly ignoreParam = [
        'locationStr',
        'listingTypeStr',
        'propertyTypeStr',
        // 'countryLocaleStr',
    ];

    constructor();
    constructor(_url: string, _additionaleParams?: string[]);
    constructor(url?: string, additionaleParams: string[] = []) {
        this.makeObj(url || '', additionaleParams);
    }

    makeObj(): void;
    makeObj(_url: string, _additionaleParams?: string[]): void;
    makeObj(url?: string, additionaleParams: string[] = []): void {
        if (url) {
            if (typeof url === 'string')
                this._parsedUrlObj = qs.parseUrl(url, parsedOption);
            else this._parsedUrlObj = url;
        }
        else {
            this._parsedUrlObj = qs.parseUrl(window.location.href, parsedOption);
        }

        this._urlObj = new URL(
            qs.stringifyUrl(this._parsedUrlObj, stringifyOption)
        );
        this._additionaleParams = additionaleParams || [];
        this._searchParams = this._parsedUrlObj.query as AddtionalParamsType<T>;

        const pathnameArr = this.urlObj.pathname
            .split('/')
            .filter((term) => !!term);

        this._pathParams = {
            locationStr: pathnameArr[1] as string,
            ...(pathnameArr.length > 2
                ? Object.fromEntries(
                    pathnameArr[2]?.split('-').map((item) => item.split('_')) as []
                )
                : {}),
            ...(pathnameArr &&
                pathnameArr.length > 0 &&
                Object.fromEntries(
                    ((pathnameArr[0] as string).includes('-')
                        ? (pathnameArr[0] as string).split('-').map((item, key) => {
                            return key === 0
                                ? [
                                    'listingTypeStr',
                                    pathnameArr[0].includes('buy') ? 'buy' : 'rent',
                                ]
                                : ['propertyTypeStr', item];
                        })
                        : [['listingTypeStr', pathnameArr[0] as string]]) as []
                )),
        };
    }

    extracted() {
        const params = pick({...this._searchParams, ...this._pathParams}, [
            ...defaultParams,
            ...this._additionaleParams,
        ]);

        return params;
    }

    clear(
        withOut: {
            pathParams: string[];
            queryParams: string[];
        } = {
            pathParams: this.ignoreParam,
            queryParams: [],
        }
    ) {
        const filteredQueryParam = queryParams
            .slice()
            .filter((item) => !withOut.queryParams.includes(item));
        const filteredPathParams = pathParams
            .slice()
            .filter((item) => !withOut.pathParams.includes(item));

        filteredQueryParam.forEach((item) => {
            this.modify(item, '', 'queryParam');
        });
        filteredPathParams.forEach((item) => {
            if (item) this.modify(item, '', 'pathParam');
        });
    }

    modify(
        _param: keyof UrlParamsType | keyof AddtionalParamsType<T>,
        _value: string | number | (string | number)[],
        _mode?: 'pathParam' | 'queryParam'
    ): void;
    modify(
        _param: string,
        _value: string | number | (string | number)[],
        _mode?: 'pathParam' | 'queryParam'
    ): void;
    modify(
        param: any,
        value: string | number | (string | number)[],
        mode: 'pathParam' | 'queryParam' = 'queryParam'
    ): void {
        let proccessValue: any;
        if (Array.isArray(value)) {
            proccessValue = value.join(',');
        }
        else if (typeof value === 'number') {
            proccessValue = value.toString();
        }
        else {
            proccessValue = value;
        }
        // value = typeof value === 'string' ? value : value.join(',');
        if (mode === 'pathParam') {
            this._pathParams[param as keyof UrlParamsType] = proccessValue;
            // this._urlObj.search = qs.stringify(this._searchParams)
        }
        else {
            this._searchParams[param as keyof AddtionalParamsType<T>] = proccessValue;
            this._urlObj.search = qs.stringify(this._searchParams);
        }
    }

    get pathParams() {
        return this._pathParams;
    }

    set pathParams(value) {
        this._pathParams = value;
    }

    get searchParamsStr() {
        const searchParam = Object.keys(this.parsedUrlObj.query).reduce<Record<string, any>>((result, key) => {
            if (this.parsedUrlObj.query[key]) {
                result[key] = this.parsedUrlObj.query[key];
            }

            return result;
        }, {});

        return !isEmpty(this.parsedUrlObj.query)
            ? `${
                !isEmpty(searchParam)
                    ? '?' + qs.stringify(searchParam, stringifyOption)
                    : ''
            }`
            : '';
    }

    get searchParams() {
        return this._searchParams;
    }

    get parsedUrlObj() {
        return this._parsedUrlObj;
    }

    get url() {
        return qs.stringifyUrl(this.parsedUrlObj, stringifyOption);
    }

    get urlObj() {
        return this._urlObj;
    }

    get fullPathname() {
        const combinePath = chain(this.pathParams)
            .omit(this.ignoreParam)
            .entries()
            .value()
            .filter((item) => item[1])
            .map((item) => item.join('_'))
            .join('-');

        const searchParam = Object.keys(this.parsedUrlObj.query).reduce<Record<string, any>>((result, key) => {
            if (this.parsedUrlObj.query[key]) {
                result[key] = this.parsedUrlObj.query[key];
            }

            return result;
        }, {});

        // listingType and propertyType
        const partOne = `${this.pathParams.listingTypeStr}${
            this.pathParams.propertyTypeStr
                ? `-${this.pathParams.propertyTypeStr}`
                : ''
        }`;

        // location
        const partTwo = this.pathParams.locationStr as string;

        // additional pathParams
        const partThree = combinePath.length ? `/${combinePath}` : '';

        // queryParams

        const partFour = !isEmpty(this.parsedUrlObj.query)
            ? `${
                !isEmpty(searchParam)
                    ? '?' + qs.stringify(searchParam, stringifyOption)
                    : ''
            }`
            : '';

        const path = `${partOne}/${partTwo}${partThree}${partFour}`;

        return path;
    }
}
